<template>
  <button
    :class="buttonClasses"
    :disabled="disabled || loading"
    :aria-label="ariaLabel || text"
    :type="type"
    @click="handleClick"
  >
    <!-- Loading Spinner -->
    <div v-if="loading" class="base-button__spinner">
      <div class="spinner"></div>
    </div>
    
    <!-- Icon (before text) -->
    <font-awesome-icon class="base-button__icon base-button__icon--before" :icon="icon" v-if="icon && !loading && iconPosition === 'before'" />
    
    <!-- Button Text -->
    <span v-if="text" class="base-button__text">{{ text }}</span>
    
    <!-- Icon (after text) -->
    <font-awesome-icon class="base-button__icon base-button__icon--after" :icon="icon" v-if="icon && !loading && iconPosition === 'after'" />
    
    <!-- Slot for custom content - can be used with or without text/icon -->
    <slot></slot>
  </button>
</template>

<script>
/**
 * BaseButton - A reusable, customizable button component
 * 
 * This component provides a flexible button implementation with support for:
 * - Multiple visual variants (primary, secondary, outline, etc.)
 * - Different sizes (small, medium, large)
 * - Loading states with spinner
 * - Icons with configurable positioning
 * - Full accessibility support
 * - Custom click handling with event emission
 * 
 * @component
 * @example
 * <!-- Basic usage -->
 * <BaseButton 
 *   text="Create New App" 
 *   icon="plus" 
 *   @click="handleCreateApp" 
 * />
 * 
 * @example
 * <!-- With loading state -->
 * <BaseButton 
 *   text="Saving..." 
 *   :loading="isSaving" 
 *   variant="primary" 
 *   size="large"
 * />
 * 
 * @example
 * <!-- Outline variant with custom styling -->
 * <BaseButton 
 *   text="Cancel" 
 *   variant="outline" 
 *   size="small" 
 *   @click="handleCancel"
 * />
 */
export default {
  name: 'BaseButton',
  
  props: {
    /**
     * The text content of the button
     * @type {String}
     */
    text: {
      type: String,
      default: ''
    },
    
    /**
     * Icon name (e.g., 'plus', 'edit')
     * @type {String}
     */
    icon: {
      type: String,
      default: ''
    },
    
    /**
     * Position of the icon relative to text
     * @type {String}
     * @values 'before', 'after'
     */
    iconPosition: {
      type: String,
      default: 'before',
      validator: (value) => ['before', 'after'].includes(value)
    },
    
    /**
     * Visual style variant of the button
     * @type {String}
     * @values 'primary', 'secondary', 'outline', 'danger', 'success', 'warning'
     */
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'outline', 'danger', 'success', 'warning'].includes(value)
    },
    
    /**
     * Size of the button
     * @type {String}
     * @values 'small', 'medium', 'large'
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    
    /**
     * Whether the button is disabled
     * @type {Boolean}
     */
    disabled: {
      type: Boolean,
      default: false
    },
    
    /**
     * Whether the button is in loading state
     * @type {Boolean}
     */
    loading: {
      type: Boolean,
      default: false
    },
    
    /**
     * Whether the button should take full width of its container
     * @type {Boolean}
     */
    block: {
      type: Boolean,
      default: false
    },
    
    /**
     * HTML button type attribute
     * @type {String}
     * @values 'button', 'submit', 'reset'
     */
    type: {
      type: String,
      default: 'button',
      validator: (value) => ['button', 'submit', 'reset'].includes(value)
    },
    
    /**
     * ARIA label for accessibility
     * @type {String}
     */
    ariaLabel: {
      type: String,
      default: ''
    }
  },
  
  emits: [
    /**
     * Emitted when button is clicked
     * @param {Event} event - The click event
     */
    'click'
  ],
  
  computed: {
    /**
     * Computed classes for the button element
     * @returns {Array} Array of CSS classes
     */
    buttonClasses() {
      return [
        'base-button',
        `base-button--${this.variant}`,
        `base-button--${this.size}`,
        {
          'base-button--block': this.block,
          'base-button--disabled': this.disabled,
          'base-button--loading': this.loading,
          'base-button--icon-only': this.icon && !this.text,
          'base-button--has-icon': this.icon && this.text
        }
      ]
    }
  },
  
  methods: {
    /**
     * Handles button click events
     * Prevents click handling when button is disabled or loading
     * @param {Event} event - The click event
     */
    handleClick(event) {
      if (this.disabled || this.loading) {
        event.preventDefault()
        return
      }
      
      this.$emit('click', event)
    }
  }
}
</script>

<style scoped>
/**
 * BaseButton Component Styles
 * 
 * Provides comprehensive styling for the BaseButton component including:
 * - Base button styles with proper typography and spacing
 * - Multiple visual variants with distinct color schemes
 * - Size variations for different use cases
 * - Interactive states (hover, focus, active, disabled)
 * - Loading state with animated spinner
 * - Accessibility features and focus indicators
 * - Responsive design considerations
 */

/* ===== BASE BUTTON STYLES ===== */
.base-button {
  /* Layout & Display */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  /* Typography */
  font-family: inherit;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  
  /* Interaction */
  cursor: pointer;
  user-select: none;
  
  /* Borders & Spacing */
  border: 1px solid transparent;
  border-radius: 8px;
  
  /* Transitions */
  transition: all 0.2s ease-in-out;
  
  /* Focus outline */
  outline: none;
}

/* ===== SIZE VARIATIONS ===== */
.base-button--small {
  padding: 8px 16px;
  font-size: 14px;
  line-height: 1.4;
  gap: 6px;
}

.base-button--medium {
  padding: 12px 24px;
  font-size: 16px;
  line-height: 1.5;
  gap: 8px;
}

.base-button--large {
  padding: 16px 32px;
  font-size: 18px;
  line-height: 1.5;
  gap: 10px;
}

/* Icon-only buttons (no text) */
.base-button--icon-only.base-button--small {
  padding: 8px;
}

.base-button--icon-only.base-button--medium {
  padding: 12px;
}

.base-button--icon-only.base-button--large {
  padding: 16px;
}

/* ===== VARIANT STYLES ===== */

/* Primary Button (matches original Create New App button) */
.base-button--primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-color: #3b82f6;
  color: white;
}

.base-button--primary:hover:not(.base-button--disabled):not(.base-button--loading) {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.base-button--primary:active:not(.base-button--disabled):not(.base-button--loading) {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

/* Secondary Button */
.base-button--secondary {
  background-color: #6b7280;
  border-color: #6b7280;
  color: white;
}

.base-button--secondary:hover:not(.base-button--disabled):not(.base-button--loading) {
  background-color: #4b5563;
  border-color: #4b5563;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(107, 114, 128, 0.3);
}

/* Outline Button */
.base-button--outline {
  background-color: transparent;
  border-color: #d1d5db;
  color: #374151;
}

.base-button--outline:hover:not(.base-button--disabled):not(.base-button--loading) {
  background-color: #f9fafb;
  border-color: #9ca3af;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Danger Button */
.base-button--danger {
  background-color: #dc2626;
  border-color: #dc2626;
  color: white;
}

.base-button--danger:hover:not(.base-button--disabled):not(.base-button--loading) {
  background-color: #b91c1c;
  border-color: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

/* Success Button */
.base-button--success {
  background-color: #059669;
  border-color: #059669;
  color: white;
}

.base-button--success:hover:not(.base-button--disabled):not(.base-button--loading) {
  background-color: #047857;
  border-color: #047857;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
}

/* Warning Button */
.base-button--warning {
  background-color: #d97706;
  border-color: #d97706;
  color: white;
}

.base-button--warning:hover:not(.base-button--disabled):not(.base-button--loading) {
  background-color: #b45309;
  border-color: #b45309;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(217, 119, 6, 0.3);
}

/* ===== STATE STYLES ===== */

/* Disabled State */
.base-button--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

/* Loading State */
.base-button--loading {
  cursor: wait;
  transform: none !important;
}

.base-button--loading .base-button__text,
.base-button--loading .base-button__icon {
  opacity: 0.7;
}

/* Focus State (for accessibility) */
.base-button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

/* ===== BLOCK BUTTON ===== */
.base-button--block {
  width: 100%;
  display: flex;
}

/* ===== ICON STYLES ===== */
.base-button__icon {
  flex-shrink: 0;
}

.base-button__icon--before {
  margin-right: 0; /* Gap is handled by parent flex gap */
}

.base-button__icon--after {
  margin-left: 0; /* Gap is handled by parent flex gap */
}

/* ===== TEXT STYLES ===== */
.base-button__text {
  flex-shrink: 0;
}

/* ===== LOADING SPINNER ===== */
.base-button__spinner {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: base-button-spin 1s linear infinite;
}

@keyframes base-button-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Adjust spinner size for different button sizes */
.base-button--small .spinner {
  width: 14px;
  height: 14px;
}

.base-button--large .spinner {
  width: 18px;
  height: 18px;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 768px) {
  .base-button--large {
    padding: 14px 28px;
    font-size: 16px;
  }
  
  .base-button--medium {
    padding: 10px 20px;
    font-size: 15px;
  }
}

@media (max-width: 480px) {
  .base-button--large {
    padding: 12px 24px;
    font-size: 15px;
  }
  
  .base-button--medium {
    padding: 9px 18px;
    font-size: 14px;
  }
  
  /* On small screens, reduce transform effects */
  .base-button:hover:not(.base-button--disabled):not(.base-button--loading) {
    transform: none;
  }
}

/* ===== HIGH CONTRAST MODE SUPPORT ===== */
@media (prefers-contrast: more) {
  .base-button {
    border-width: 2px;
  }
  
  .base-button:focus-visible {
    outline-width: 3px;
  }
}

/* ===== REDUCED MOTION SUPPORT ===== */
@media (prefers-reduced-motion: reduce) {
  .base-button {
    transition: none;
  }
  
  .base-button:hover:not(.base-button--disabled):not(.base-button--loading) {
    transform: none;
  }
  
  .spinner {
    animation: none;
  }
}
</style>
